import { type FC, type ReactNode } from 'react';
import { type OpeningHours, Weekday, type WeekdayTime } from '../api';
import { useFieldContext } from './contexts';
import { Field } from './Field';

const WeekdayFieldRow: FC<{
  readonly label: string | undefined;
  readonly children: ReactNode;
}> = ({ label, children }) => (
  <div className={'field is-horizontal'}>
    <div className={'field-label is-small'}>
      <label className={'label'}>{label}</label>
    </div>
    <div className={'field-body'}>{children}</div>
  </div>
);

const LoadingWeekdayField: FC<{
  readonly weekday: string;
}> = ({ weekday }) => (
  <WeekdayFieldRow label={weekday}>
    <div className={'field'}>
      <div className={'control is-loading'}>
        <input type={'text'} className={'input is-small is-static'} value={'Loading…'} readOnly />
      </div>
    </div>
  </WeekdayFieldRow>
);

const ClosedWeekdayField: FC<{
  readonly weekday: string;
}> = ({ weekday }) => (
  <WeekdayFieldRow label={weekday}>
    <div className={'field'}>
      <div className={'control'}>
        <input type={'text'} className={'input is-small is-static'} value={'Closed'} readOnly />
      </div>
    </div>
  </WeekdayFieldRow>
);

const Hour: FC<{
  readonly isDisabled: boolean;
  readonly hour: WeekdayTime;
}> = ({ hour, isDisabled }) => (
  <div className={'field'}>
    <div className={'control'}>
      <input
        type={'time'}
        className={'input is-small is-static'}
        value={hour.time}
        readOnly
        disabled={isDisabled}
        aria-disabled={isDisabled}
      />
    </div>
  </div>
);

const OpenWeekdayField: FC<{
  readonly isDisabled: boolean;
  readonly label: string | undefined;
  readonly openingHours: OpeningHours;
}> = ({ isDisabled, label, openingHours }) => (
  <WeekdayFieldRow label={label}>
    <Hour isDisabled={isDisabled} hour={openingHours.open} />
    {openingHours.close ? (
      <>
        <div className={'field'}>
          <div className={'control'}>
            <input type={'text'} value={'–'} className={'input is-static is-small'} readOnly />
          </div>
        </div>
        <Hour isDisabled={isDisabled} hour={openingHours.close} />
      </>
    ) : null}
  </WeekdayFieldRow>
);

const WeekdayField: FC<{
  readonly isDisabled: boolean;
  readonly isLoading: boolean;
  readonly weekDayHours: readonly OpeningHours[];
  readonly weekday: string;
}> = ({ isDisabled, isLoading, weekday, weekDayHours }) =>
  isLoading ? (
    <LoadingWeekdayField weekday={weekday} />
  ) : weekDayHours.length === 0 ? (
    <ClosedWeekdayField weekday={weekday} />
  ) : (
    weekDayHours.map((weekDayOpeningHours, localIndex) => (
      <OpenWeekdayField
        key={localIndex}
        label={localIndex === 0 ? weekday : undefined}
        openingHours={weekDayOpeningHours}
        isDisabled={isDisabled}
      />
    ))
  );

interface Props {
  readonly isDisabled: boolean;
  readonly isLoading: boolean;
  readonly label: string;
}

export const OpeningHoursField: FC<Props> = ({ isDisabled, isLoading, label }: Props) => {
  const field = useFieldContext<OpeningHours[]>();
  const forDay = (weekDay: Weekday) => field.state.value.filter((oh) => oh.open.day === weekDay);
  return (
    <Field label={label} name={field.name}>
      <WeekdayField
        weekday={'Monday'}
        weekDayHours={forDay(Weekday.Monday)}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
      <WeekdayField
        weekday={'Tuesday'}
        weekDayHours={forDay(Weekday.Tuesday)}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
      <WeekdayField
        weekday={'Wednesday'}
        weekDayHours={forDay(Weekday.Wednesday)}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
      <WeekdayField
        weekday={'Thursday'}
        weekDayHours={forDay(Weekday.Thursday)}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
      <WeekdayField
        weekday={'Friday'}
        weekDayHours={forDay(Weekday.Friday)}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
      <WeekdayField
        weekday={'Saturday'}
        weekDayHours={forDay(Weekday.Saturday)}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
      <WeekdayField
        weekday={'Sunday'}
        weekDayHours={forDay(Weekday.Sunday)}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
    </Field>
  );
};
